import React, { useMemo } from 'react';
import DataTable from './DataTable';
import WidgetBase from './WidgetBase';
import { useWidgetData } from '../../hooks/useWidgetData';
import { exportToCSV } from '../../utils/exportToCSV';

/**
 * Table Widget Component
 * Modernized replacement for List.js
 * Pure table display with sorting, filtering, pagination
 * Most flexible - handles all table types
 */
function TableWidget({ data, handlechangea }) {
    // Fetch data using custom hook
    const { data: apiData, loading, error } = useWidgetData(
        data.url,
        data.RequestJson,
        data.RequestJson1
    );

    // Transform API data to table format
    const { tableData, columns } = useMemo(() => {
        if (!apiData || apiData.length === 0) {
            return { tableData: [], columns: [] };
        }

        try {
            // API typically returns data in columnDataSet format
            const dataset = apiData[0].columnDataSet1 || apiData[0].columnData || apiData;
            
            if (!dataset || dataset.length === 0) {
                return { tableData: [], columns: [] };
            }

            // Extract columns from first dataset item
            const firstItem = Array.isArray(dataset) ? dataset[0] : dataset;
            const cols = firstItem.datasets?.map((ds, idx) => ({
                key: `col${idx}`,
                label: ds.label || `Column ${idx + 1}`,
                sortable: true,
                format: (val) => {
                    if (typeof val === 'number') {
                        // Format numbers with commas
                        return val.toLocaleString();
                    }
                    return val || '-';
                }
            })) || [];

            // Build table rows
            const rows = [];
            if (Array.isArray(dataset)) {
                dataset.forEach(item => {
                    if (item.datasets) {
                        const row = {};
                        item.datasets.forEach((ds, idx) => {
                            row[`col${idx}`] = ds.data;
                        });
                        rows.push(row);
                    }
                });
            }

            return { tableData: rows, columns: cols };
        } catch (err) {
            console.error('Error transforming table data:', err);
            return { tableData: [], columns: [] };
        }
    }, [apiData]);

    // Handle row click for drill-down
    const handleRowClick = (row) => {
        if (handlechangea) {
            // Trigger filter change based on first column value
            const firstValue = row.col0;
            if (firstValue) {
                handlechangea({ target: { value: firstValue } });
            }
        }
    };

    // Export handler
    const handleExport = () => {
        if (tableData.length > 0) {
            exportToCSV(tableData, data.title || 'table-data', columns);
        }
    };

    return (
        <WidgetBase
            loading={loading}
            error={error}
            chartComponent={null} // Table-only widget
            tableComponent={
                tableData.length > 0 ? (
                    <DataTable
                        columns={columns}
                        data={tableData}
                        onRowClick={handlechangea ? handleRowClick : null}
                        pagination={{ enabled: true, itemsPerPage: 15 }}
                        sortable={true}
                        searchable={true}
                    />
                ) : null
            }
            onExport={handleExport}
            showToggle={false} // No toggle for table-only
            showExport={true}
        />
    );
}

export default TableWidget;
